<template>
  <section class="StatusDisplayBar">
    <v-alert class="status-alert" :value="hasError" type="error">
      <span v-if="typeof error === 'string'">
        {{ error }}
      </span>
      <ul v-else :class="error && error.length == 1 ? 'list-style-none' : ''">
        <li v-for="(err, errIndex) in error" :key="errIndex">
          {{ err }}
        </li>
      </ul>
    </v-alert>
    <v-alert class="status-alert" :value="hasWarning" type="warning">
      <span v-if="typeof warnMessage === 'string'">
        {{ warnMessage }}
      </span>
      <ul v-else>
        <li v-for="(warn, warnIndex) in warnMessage" :key="warnIndex">
          {{ warn }}
        </li>
      </ul>
    </v-alert>
    <v-alert class="status-alert" :value="hasWarningData" type="warning">
      <span>
        {{ warningText }}
      </span>
      <ul>
        <li v-for="(warn, warnDataIndex) in warningData" :key="warnDataIndex">
          {{ warn }}
        </li>
      </ul>
    </v-alert>
    <v-alert class="status-alert" :value="hasSuccess" type="success">
      <slot name="SuccessMessage">
        {{ successMessage }}
      </slot>
    </v-alert>
    <v-alert
      v-if="hasLoadMessage"
      class="status-alert"
      :value="loading"
      type="info"
    >
      {{ loadingMessage }}
    </v-alert>
    <v-progress-linear v-if="loading" indeterminate color="success" />
  </section>
</template>

<script>
/**
 * This component is used to display a loading bar or error message,
 * typically to show that an API call is pending or problematic
 * "error" should be either the error message or "undefined"
 */
export default {
  name: 'StatusDisplayBar',
  props: {
    loading: {
      type: Boolean,
    },
    error: {
      type: [String, Array],
      default() {
        return undefined;
      },
    },
    loadingMessage: {
      type: String,
      default() {
        return undefined;
      },
    },
    warnMessage: {
      type: String,
      default() {
        return undefined;
      },
    },
    warningText: {
      type: String,
      default() {
        return undefined;
      },
    },
    warningData: {
      type: Array,
      default() {
        return [];
      },
    },
    successMessage: {
      type: String,
      default() {
        return undefined;
      },
    },
  },
  computed: {
    hasError() {
      if (this.error instanceof Array) {
        return this.error.length > 0;
      }
      return this.error !== undefined && this.error !== '';
    },
    hasWarning() {
      return this.warnMessage !== undefined && this.warnMessage !== '';
    },
    hasWarningData() {
      return (
        this.warningData !== undefined &&
        this.warningData.length !== 0 &&
        this.warningText !== undefined &&
        this.warningText !== ''
      );
    },
    hasLoadMessage() {
      return this.loadingMessage !== undefined && this.loadingMessage !== '';
    },
    hasSuccess() {
      return this.successMessage !== undefined && this.successMessage !== '';
    },
  },
};
</script>
<style>
.v-progress-linear,
.v-progress-linear__bar__indeterminate {
  border-radius: 6px;
}

.StatusDisplayBar {
  width: 100%;
}

.list-style-none {
  list-style: none;
  padding-left: 0;
}

.status-alert.v-alert.error {
  background-color: var(--sb-compliance-red-bg) !important;
  color: var(--sb-compliance-red-text) !important;
  border: none !important;
  border-left: 3px solid var(--sb-compliance-red) !important;
}

.StatusDisplayBar .status-alert.v-alert.error .v-alert__icon.v-icon {
  color: var(--sb-compliance-red) !important;
  opacity: 1 !important;
}

.status-alert.v-alert.warning {
  background-color: var(--sb-compliance-amber-bg) !important;
  color: var(--sb-compliance-amber-text) !important;
  border: none !important;
  border-left: 3px solid var(--sb-compliance-amber) !important;
}

.StatusDisplayBar .status-alert.v-alert.warning .v-alert__icon.v-icon {
  color: var(--sb-compliance-amber) !important;
  opacity: 1 !important;
}

.status-alert.v-alert.success {
  background-color: var(--sb-compliance-green-bg) !important;
  color: var(--sb-compliance-green-text) !important;
  border: none !important;
  border-left: 3px solid var(--sb-compliance-green) !important;
}

.StatusDisplayBar .status-alert.v-alert.success .v-alert__icon.v-icon {
  color: var(--sb-compliance-green) !important;
  opacity: 1 !important;
}

.status-alert.v-alert.info {
  background-color: var(--sb-compliance-blue-bg) !important;
  color: var(--sb-compliance-blue) !important;
  border: none !important;
  border-left: 3px solid var(--sb-compliance-blue) !important;
}

.StatusDisplayBar .status-alert.v-alert.info .v-alert__icon.v-icon {
  color: var(--sb-compliance-blue) !important;
  opacity: 1 !important;
}

.StatusDisplayBar .v-progress-linear__background {
  background-color: var(--sb-compliance-blue) !important;
}

.StatusDisplayBar .v-progress-linear__bar__indeterminate .long,
.StatusDisplayBar .v-progress-linear__bar__indeterminate .short {
  background-color: var(--sb-compliance-blue) !important;
}
</style>
